import { Box, Modal } from "@mui/material";
import { Close, FileDownload, Link, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useState, useEffect, useRef } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { ButtonV1 } from "../atoms/button";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./ImageGalleryModal.css";
import { downloadImagesAsZip } from "../../utils/helper";

interface ImageGalleryModalProps {
  open: boolean;
  onClose: VoidFunction;
  item: {
    stage: string;
    title: string;
    newTitle?: string;
    thumbLink: string;
    dt: string;
    orientation: string;
    imageGallery: string[];
    creativeId: string;
  };
}

export function ImageGalleryModal({ open, onClose, item }: ImageGalleryModalProps) {
  const [copy, setCopy] = useState(false);
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const sliderRef = useRef<Slider>(null);

  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90vw",
    maxWidth: "90vw",
    height: "70vh",
    maxHeight: "90vh",
    bgcolor: "transparent",
    border: "none",
    boxShadow: "none",
    p: 0,
    outline: "none"
  };

  const newTitle = encodeURI(item?.title).replaceAll("+", "%2B");

  const handleClick = (name: string) => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_${name}`,
        category: "User Interaction",
        action: "Button Click",
        label: `${name} Button`,
      });
    }
  };

  const handleModalClose = () => {
    setCurrentSlide(0);
    onClose();
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open) return;

      switch (event.key) {
        case 'ArrowLeft':
          sliderRef.current?.slickPrev();
          break;
        case 'ArrowRight':
          sliderRef.current?.slickNext();
          break;
        case 'Escape':
          handleModalClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    beforeChange: (oldIndex: number, newIndex: number) => setCurrentSlide(newIndex),
    customPaging: (i: number) => (
      <div
        className={`w-3 h-3 mx-1 rounded-sm transition-all duration-200 ${i === currentSlide ? "bg-black" : "bg-black/30 border border-black/50"
          }`}
      />
    ),
    prevArrow: <button className="absolute left-3 top-1/2 transform -translate-y-1/2 z-30 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full transition-all duration-200 shadow-lg">
      <ChevronLeft className="h-10 !text-xl w-10 text-white md:text-black" />
    </button>,
    nextArrow: <button className="absolute right-5 top-1/2 transform -translate-y-1/2 z-30 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full transition-all duration-200 shadow-lg">
      <ChevronRight className="h-10 w-10 text-white md:text-black" />
    </button>,
    dotsClass: "slick-dots custom-dots !right-0 !bottom-0 !p-4 w-full !flex !justify-end !items-center",
    autoplay: false,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          arrows: false,
          dots: true
        }
      }
    ]
  };

  const sliderSettings2 = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    beforeChange: (oldIndex: number, newIndex: number) => setCurrentSlide(newIndex),
    customPaging: (i: number) => (
      <div
        className={`w-3 h-3 mx-1 rounded-sm transition-all duration-200 ${i === currentSlide ? "bg-black" : "bg-black/30 border border-black/50"
          }`}
      />
    ),
    prevArrow: <button className="absolute left-3 top-1/2 transform -translate-y-1/2 z-30 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full transition-all duration-200 shadow-lg">
      <ChevronLeft className="!h-10 !w-10 text-black" />
    </button>,
    nextArrow: <button className="absolute right-5 top-1/2 transform -translate-y-1/2 z-30 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full transition-all duration-200 shadow-lg">
      <ChevronRight className="!h-10 !w-10 text-black" />
    </button>,
    dotsClass: "slick-dots custom-dots !right-0 !bottom-0 !p-4 w-full !flex !justify-end !items-center",
    autoplay: false,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          arrows: false,
          dots: true
        }
      }
    ]
  };

  if (!item.imageGallery || item.imageGallery.length === 0) {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.7) !important",
        }
      }}
    >
      {
        item.orientation === "Vertical / Portrait" ? (
          <Box sx={style} className="!w-[95vw] !max-w-[95vw] !h-auto md:!h-full md:!w-[75vw] md:!max-w-[75vw] lg:!w-[50vw] lg:!max-w-[50vw] !p-0 overflow-hidden portrait-image-gallery-modal" borderRadius={"1.5rem"}>
            <div className="w-full h-full flex flex-col md:flex-row gap-0 portrait-image-gallery-modal relative">
              <div
                className="absolute right-0.5 top-0.5 bg-black/40 hover:bg-black/60 rounded-full cursor-pointer p-2 z-20 transition-all duration-200"
                onClick={handleModalClose}
              >
                <Close className="text-white !text-3xl" />
              </div>
              <div className="w-full md:w-[60%] h-auto overflow-hidden relative bg-white flex items-center justify-center">
                <div className="flex-1 h-full relative rounded-t-3xl overflow-hidden">
                  <div className="relative image-gallery-modal h-full image-gallery-modal2">
                    <Slider
                      ref={sliderRef}
                      {...sliderSettings2}
                      className="h-full"
                    >
                      {item.imageGallery.map((image, index) => (
                        <div key={index} className="h-full flex items-center justify-center relative">
                          {/* Main Image */}
                          <div className="w-full h-full flex items-center justify-center">
                            <img
                              src={image}
                              alt={`Gallery image ${index + 1}`}
                              className="w-full h-full max-h-[70vh] md:max-h-[95vh] object-contain shadow-2xl"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = "https://via.placeholder.com/800x600/8B5CF6/FFFFFF?text=Image+Error";
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </Slider>
                    <div className="verical-frame-count">{currentSlide + 1}/{item.imageGallery.length} Frames</div>
                  </div>
                  <div className="vertical-bottom"></div>
                </div>
              </div>
              <div className="bg-gray-100 w-full md:w-[50%] h-auto self-stretch flex md:flex-col flex-row gap-2 py-4 pt-8 px-2 justify-between items-start">
                <div className="flex-1 flex flex-col px-4 py-2">
                  <div className="flex items-center gap-1 font-medium text-sm text-gray-700">
                    <span
                      className={`w-2 h-2 rounded-full ${item.stage === "postsale" ? "bg-black" : "bg-gray-500"
                        }`}
                    ></span>
                    {item.stage}
                    {item.dt ? ` | ${item.dt}` : ""}
                  </div>
                  <h1 className="truncate text-wrap text-xl font-medium">{item.title}</h1>
              </div>

                <div className="flex md:gap-4 relative md:px-4 items-center justify-between">
                  <ButtonV1 className="scale-75 md:scale-100 w-10 h-10 md:w-auto md:h-auto">
                    <CopyToClipboard
                      // text={
                      //   `${window.location.origin}/imageGallery?images=${encodeURI(JSON.stringify(item.imageGallery))}&title=${encodeURIComponent(newTitle)}`
                      // }
                      text={
                        `${window.location.origin}/imageGallery?creativeId=${item.creativeId}`
                      }
                      onCopy={() => {
                        handleClick("CopyLink");
                        setCopy(true);
                      }}
                    >
                      {copy ? (
                        <p className="capitalize">copied</p>
                      ) : (
                        <Link className="text-white" />
                      )}
                    </CopyToClipboard>
                  </ButtonV1>
                  <ButtonV1 className="scale-75 md:scale-100">
                    <button
                      onClick={() => downloadImagesAsZip(item.imageGallery, item.newTitle || item.title)}
                      className="flex items-center justify-center"
                    >
                      <FileDownload className="text-white" />
                    </button>
                  </ButtonV1>
                </div>
              </div>
            </div>
          </Box>
        ) : (
          <Box sx={style} className="relative rounded-3xl overflow-hidden !bg-transparent !h-max landscape-image-gallery-modal">
            {/* Main Content Area */}
            <div className="w-full h-full bg-white flex flex-col relative">
              <div
                className="absolute right-0.5 top-0.5 bg-black/40 hover:bg-black/60 rounded-full cursor-pointer p-2 z-20 transition-all duration-200"
                onClick={handleModalClose}
              >
                <Close className="text-white !text-3xl" />
              </div>
              {/* Image Carousel Section */}
              <div className="flex-1 relative rounded-t-3xl overflow-hidden">
                <div className="relative image-gallery-modal">
                  <Slider
                    ref={sliderRef}
                    {...sliderSettings}
                    className="h-max"
                  >
                    {item.imageGallery.map((image, index) => (
                      <div key={index} className="h-full flex items-center justify-center relative img-overlay">
                        {/* Main Image */}
                        <div className="w-full h-full flex items-center justify-center">
                          <img
                            src={image}
                            alt={`Gallery image ${index + 1}`}
                            className="w-auto h-full max-h-[70vh] object-contain"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = "https://via.placeholder.com/800x600/8B5CF6/FFFFFF?text=Image+Error";
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </Slider>
                  <div className="verical-frame-count">{currentSlide + 1}/{item.imageGallery.length} Frames</div>
                </div>
              </div>

              {/* Footer Section */}
              <div className="w-full h-max flex gap-2 justify-between items-center bg-white-100 bottom-pillar">
                <div className="flex-1 flex flex-col px-4 py-2">
                  <div className="flex items-center gap-1 font-medium text-sm text-gray-700">
                    <span
                      className={`w-2 h-2 rounded-full ${item.stage === "postsale" ? "bg-black" : "bg-gray-500"
                        }`}
                    ></span>
                    {item.stage}
                    {item.dt ? ` | ${item.dt}` : ""}
                  </div>
                  <h1 className="truncate text-wrap text-xl font-medium">{item.title}</h1>
              </div>

                <div className="flex md:gap-4 relative md:px-4 items-center justify-between">
                  <ButtonV1 className="scale-75 md:scale-100">
                    <CopyToClipboard
                      // text={
                      //   `${window.location.origin}/imageGallery?images=${encodeURI(JSON.stringify(item.imageGallery))}&title=${encodeURIComponent(newTitle)}`
                      // }
                      text={
                        `${window.location.origin}/imageGallery?creativeId=${item.creativeId}`
                      }
                      onCopy={() => {
                        handleClick("CopyLink");
                        setCopy(true);
                      }}
                    >
                      {copy ? (
                        <p className="capitalize">copied</p>
                      ) : (
                        <Link className="text-white" />
                      )}
                    </CopyToClipboard>
                  </ButtonV1>
                  <ButtonV1 className="scale-75 md:scale-100">
                    <button
                      onClick={() => downloadImagesAsZip(item.imageGallery, item.newTitle || item.title)}
                      className="flex items-center justify-center"
                    >
                      <FileDownload className="text-white" />
                    </button>
                  </ButtonV1>
                </div>
              </div>
            </div>
          </Box>
        )
      }
    </Modal>
  );
}
